import React, { useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { 
  faTimes, faCreditCard, faStar, faCheckCircle, 
  faExclamationTriangle, faSpinner, faArrowRight, faArrowLeft
} from '@fortawesome/free-solid-svg-icons'
import { CoordinatorUpgradeService } from '../services/coordinatorUpgradeService'
import { CoordinatorPaymentService } from '../services/coordinatorPaymentService'
import { CoordinatorProfile, CoordinatorCredits } from '../lib/supabase'
import { CoordinatorPaymentSetupForm } from '../types/coordinatorPayment'
import CoordinatorPaymentSetup from './CoordinatorPaymentSetup'

interface CoordinatorUpgradeModalProps {
  isOpen: boolean
  onClose: () => void
  userEmail: string
  userName: string
}

export default function CoordinatorUpgradeModal({
  isOpen,
  onClose,
  userEmail,
  userName
}: CoordinatorUpgradeModalProps) {
  const [step, setStep] = useState<'overview' | 'payment-setup' | 'payment'>('overview')
  const [loading, setLoading] = useState(false)
  const [profile, setProfile] = useState<CoordinatorProfile | null>(null)
  const [credits, setCredits] = useState<CoordinatorCredits[]>([])
  const [availableCredits, setAvailableCredits] = useState(0)
  const [isCoordinator, setIsCoordinator] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasPaymentConfig, setHasPaymentConfig] = useState(false)
  const [paymentConfig, setPaymentConfig] = useState<CoordinatorPaymentSetupForm | null>(null)

  const upgradeCost = CoordinatorUpgradeService.getUpgradeCost()
  const currency = CoordinatorUpgradeService.getCurrency()

  useEffect(() => {
    if (isOpen && userEmail) {
      loadUserData()
    }
  }, [isOpen, userEmail])

  const loadUserData = async () => {
    try {
      setLoading(true)
      setError(null)

      const [profileData, creditsData, availableCreditsCount, coordinatorStatus, existingPaymentConfig] = await Promise.all([
        CoordinatorUpgradeService.getCoordinatorProfile(userEmail),
        CoordinatorUpgradeService.getUserCredits(userEmail),
        CoordinatorUpgradeService.getAvailableCredits(userEmail),
        CoordinatorUpgradeService.isUserCoordinator(userEmail),
        CoordinatorPaymentService.getCoordinatorPaymentConfig(userEmail)
      ])

      setProfile(profileData)
      setCredits(creditsData)
      setAvailableCredits(availableCreditsCount)
      setIsCoordinator(coordinatorStatus)
      setHasPaymentConfig(!!existingPaymentConfig)
      
      if (existingPaymentConfig) {
        setPaymentConfig({
          payment_gateway: existingPaymentConfig.payment_gateway,
          payfast_merchant_id: existingPaymentConfig.payfast_merchant_id || '',
          payfast_merchant_key: existingPaymentConfig.payfast_merchant_key || '',
          payfast_passphrase: existingPaymentConfig.payfast_passphrase || '',
          payfast_sandbox: existingPaymentConfig.payfast_sandbox ?? true,
          bank_name: existingPaymentConfig.bank_name || '',
          account_holder: existingPaymentConfig.account_holder || '',
          account_number: existingPaymentConfig.account_number || '',
          branch_code: existingPaymentConfig.branch_code || '',
          account_type: existingPaymentConfig.account_type || 'Cheque',
          auto_confirm_payments: existingPaymentConfig.auto_confirm_payments ?? false,
          payment_instructions: existingPaymentConfig.payment_instructions || ''
        })
      }
    } catch (error) {
      console.error('Error loading user data:', error)
      setError('Failed to load coordinator information')
    } finally {
      setLoading(false)
    }
  }

  const handlePaymentConfigSave = (config: CoordinatorPaymentSetupForm) => {
    setPaymentConfig(config)
    setHasPaymentConfig(true)
    setStep('payment')
  }

  const handleContinueToPayment = () => {
    if (!isCoordinator && !hasPaymentConfig) {
      // New coordinator needs to set up payment configuration first
      setStep('payment-setup')
    } else {
      // Existing coordinator or payment config already exists
      setStep('payment')
    }
  }

  const handleUpgradePayment = async () => {
    try {
      console.log('=== COORDINATOR UPGRADE PAYMENT STARTED ===')
      setLoading(true)
      setError(null)

      // Check if payment configuration is required but missing
      if (!isCoordinator && !hasPaymentConfig) {
        setError('Please set up your payment configuration first.')
        setStep('payment-setup')
        setLoading(false)
        return
      }

      const currentUrl = window.location.origin
      const upgradeRequest = {
        user_email: userEmail,
        user_name: userName,
        return_url: `${currentUrl}/coordinator-upgrade-success`,
        cancel_url: `${currentUrl}/coordinator-upgrade-cancel`,
        notify_url: `${currentUrl}/api/coordinator-upgrade-notify`
      }

      console.log('Upgrade Request:', upgradeRequest)

      const { paymentUrl } = await CoordinatorUpgradeService.createUpgradePayment(upgradeRequest)
      
      console.log('Payment URL returned:', paymentUrl)
      
      // Form is submitted automatically, no need to redirect
      // The loading state will remain until PayFast redirects back
    } catch (error) {
      console.error('Error creating upgrade payment:', error)
      setError('Failed to create payment. Please try again.')
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <FontAwesomeIcon icon={faStar} className="h-6 w-6 text-orange-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {isCoordinator ? 'Coordinator Dashboard' : 'Become a Coordinator'}
              </h2>
              <p className="text-sm text-gray-600">
                {isCoordinator ? 'Manage your coordinator status and credits' : 'Upgrade to create and manage events'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <FontAwesomeIcon icon={faSpinner} className="h-8 w-8 animate-spin text-orange-600" />
              <span className="ml-2 text-gray-600">Loading...</span>
            </div>
          ) : error ? (
            <div className="flex items-center space-x-2 p-4 bg-red-50 border border-red-200 rounded-lg">
              <FontAwesomeIcon icon={faExclamationTriangle} className="h-5 w-5 text-red-600" />
              <span className="text-red-700">{error}</span>
            </div>
          ) : step === 'payment-setup' ? (
            <div>
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Set Up Payment Configuration</h3>
                <p className="text-gray-600">
                  Before becoming a coordinator, please configure how participants will pay for your events.
                </p>
              </div>
              <CoordinatorPaymentSetup
                coordinatorEmail={userEmail}
                coordinatorName={userName}
                onSave={handlePaymentConfigSave}
                onCancel={() => setStep('overview')}
                showCancelButton={true}
              />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Current Status */}
              {isCoordinator && profile ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <FontAwesomeIcon icon={faCheckCircle} className="h-5 w-5 text-green-600" />
                    <span className="font-semibold text-green-800">Active Coordinator</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Total Credits Purchased:</span>
                      <span className="ml-2 font-semibold">{profile.total_credits_purchased}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Credits Used:</span>
                      <span className="ml-2 font-semibold">{profile.total_credits_used}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Available Credits:</span>
                      <span className="ml-2 font-semibold text-green-600">{availableCredits}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Events Created:</span>
                      <span className="ml-2 font-semibold">{profile.total_events_created}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <h3 className="font-semibold text-orange-800 mb-2">Coordinator Benefits</h3>
                  <ul className="space-y-2 text-sm text-orange-700">
                    <li className="flex items-center space-x-2">
                      <FontAwesomeIcon icon={faCheckCircle} className="h-4 w-4" />
                      <span>Create and manage mountain biking events</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <FontAwesomeIcon icon={faCheckCircle} className="h-4 w-4" />
                      <span>Access to participant management tools</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <FontAwesomeIcon icon={faCheckCircle} className="h-4 w-4" />
                      <span>Event registration and payment tracking</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <FontAwesomeIcon icon={faCheckCircle} className="h-4 w-4" />
                      <span>Professional event coordinator badge</span>
                    </li>
                  </ul>
                </div>
              )}

              {/* Pricing Information */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-800 mb-3">Event Creation Credits</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Cost per Event Credit:</span>
                    <span className="font-bold text-lg">R{upgradeCost.toFixed(2)}</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    <p>• Each payment of R{upgradeCost.toFixed(2)} gives you 1 event creation credit</p>
                    <p>• Use 1 credit to create and publish 1 event</p>
                    <p>• Credits never expire and can be used anytime</p>
                    <p>• {isCoordinator ? 'Purchase additional credits as needed' : 'First payment also upgrades you to coordinator status'}</p>
                  </div>
                </div>
              </div>

              {/* Credits History */}
              {credits.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-800 mb-3">Credit History</h3>
                  <div className="space-y-2">
                    {credits.map((credit) => (
                      <div key={credit.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                        <div className="text-sm">
                          <span className="text-gray-600">
                            {new Date(credit.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="text-sm space-x-4">
                          <span className="text-gray-600">
                            Purchased: <span className="font-semibold">{credit.credits_purchased}</span>
                          </span>
                          <span className="text-gray-600">
                            Used: <span className="font-semibold">{credit.credits_used}</span>
                          </span>
                          <span className="text-green-600">
                            Remaining: <span className="font-semibold">{credit.credits_remaining}</span>
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex space-x-4">
                {step === 'overview' && (
                  <>
                    <button
                      onClick={handleContinueToPayment}
                      disabled={loading}
                      className="flex-1 bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                    >
                      <FontAwesomeIcon icon={faArrowRight} className="h-5 w-5" />
                      <span>
                        {isCoordinator 
                          ? `Continue to Payment - R${upgradeCost.toFixed(2)}`
                          : hasPaymentConfig 
                            ? `Continue to Payment - R${upgradeCost.toFixed(2)}`
                            : 'Set Up Payment & Become Coordinator'
                        }
                      </span>
                    </button>
                    <button
                      onClick={onClose}
                      className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Close
                    </button>
                  </>
                )}
                
                {step === 'payment' && (
                  <>
                    <button
                      onClick={() => setStep('overview')}
                      className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-2"
                    >
                      <FontAwesomeIcon icon={faArrowLeft} className="h-4 w-4" />
                      <span>Back</span>
                    </button>
                    <button
                      onClick={handleUpgradePayment}
                      disabled={loading}
                      className="flex-1 bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                    >
                      <FontAwesomeIcon icon={faCreditCard} className="h-5 w-5" />
                      <span>
                        {isCoordinator 
                          ? `Buy Event Credit - R${upgradeCost.toFixed(2)}`
                          : `Complete Coordinator Upgrade - R${upgradeCost.toFixed(2)}`
                        }
                      </span>
                    </button>
                  </>
                )}
              </div>

              {/* Security Notice */}
              <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
                <p className="font-semibold mb-1">Secure Payment Processing</p>
                <p>Payments are processed securely through PayFast. Your coordinator status and credits will be activated automatically upon successful payment confirmation.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
} 